import './App.css'
import './styles/globals.css'

import { StatsigProvider } from '@statsig/react-bindings'
import { TrabaLoader } from '@traba/react-components'
import { getAuth } from 'firebase/auth'
import { lazy, Suspense, useMemo } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { ActiveOrgProvider } from 'src/context/activeOrg/ActiveOrgContext'
import { useUserContext } from 'src/context/user/UserContext'
import { usePageTracking } from 'src/hooks/usePageTracking'
import { stashPostLoginRedirect } from 'src/libs/postLoginRedirect'

const EmailVerificationScreen = lazy(() =>
  import('./screens/EmailVerificationScreen').then((m) => ({
    default: m.EmailVerificationScreen,
  })),
)
const LandingScreen = lazy(() =>
  import('./screens/auth/LandingScreen').then((m) => ({
    default: m.LandingScreen,
  })),
)
const LoggedInScreen = lazy(() =>
  import('./screens/LoggedInScreen').then((m) => ({
    default: m.LoggedInScreen,
  })),
)
const LoginScreen = lazy(() =>
  import('./screens/auth/LoginScreen').then((m) => ({
    default: m.LoginScreen,
  })),
)
const VersionScreen = lazy(() =>
  import('./screens/VersionScreen').then((m) => ({
    default: m.VersionScreen,
  })),
)

export function App() {
  usePageTracking()
  const { state } = useUserContext()
  const auth = getAuth()
  const location = useLocation()
  const { isLoadingUser, isUserLoggedIn, userProfile } = state
  const shouldVerifyEmail = !auth.currentUser?.emailVerified

  const statsigUser = useMemo(
    () => ({
      userID: userProfile?.uid,
      email: userProfile?.email,
      customIDs: { companyId: userProfile?.companyId },
    }),
    [userProfile?.uid, userProfile?.email, userProfile?.companyId],
  )

  const statsigOptions = useMemo(
    () => ({
      environment: {
        tier:
          import.meta.env.VITE_ENV === 'production'
            ? 'production'
            : 'development',
      },
    }),
    [],
  )

  // Deployment-tracking page; always reachable, independent of auth state.
  if (location.pathname === '/version') {
    return (
      <Suspense fallback={<TrabaLoader />}>
        <VersionScreen />
      </Suspense>
    )
  }

  if (isLoadingUser) {
    return <TrabaLoader />
  }

  return (
    <StatsigProvider
      sdkKey={import.meta.env.VITE_STATSIG_CLIENT_KEY}
      user={statsigUser}
      options={statsigOptions}
    >
      <Suspense fallback={<TrabaLoader />}>
        {!isUserLoggedIn ? (
          <Routes>
            <Route path="/login" element={<LoginScreen />} />
            <Route path="/" element={<LandingScreen />} />
            <Route path="*" element={<RedirectToLoginPreservingPath />} />
          </Routes>
        ) : shouldVerifyEmail ? (
          <EmailVerificationScreen />
        ) : (
          <ActiveOrgProvider>
            <LoggedInScreen />
          </ActiveOrgProvider>
        )}
      </Suspense>
    </StatsigProvider>
  )
}

function RedirectToLoginPreservingPath(): JSX.Element {
  const location = useLocation()
  stashPostLoginRedirect(location.pathname + location.search)
  return <Navigate to="/login" replace />
}
